import { AfterViewInit, Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Hour, PortDto, PortService, WeatherResponse } from '@proxy/ports';
import { DatePipe } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import type { ChartOptions } from 'chart.js';
import { getPdfMake } from '../../../pdfmake_global';
import { environment } from 'src/environments/environment';

const DEFAULT_LOGO_PATH = '../assets/img/company-logo.png';

@Component({
  selector: 'app-viewweather',
  templateUrl: './view-weather.component.html',
  styleUrls: ['./view-weather.component.scss'],
})
export class ViewWeatherComponent implements AfterViewInit {
  @Input() portId: string;
  canvas: any;
  ctx: any;
  port: PortDto;
  weatherResponse: WeatherResponse;
  weatherViewModel = {} as { date: Hour[] };
  todayWeather: Hour = null;
  tabs = [];

  isVisible = false;
  tempTemp: ChartHeader[] = [];
  test: {};
  // For Line Chart

  weatherData: [];
  multi: any[];
  view: any[] = [650, 300];

  // options
  legend = true;
  showLabels = true;
  animations = true;
  xAxis = true;
  yAxis = true;
  showYAxisLabel = true;
  showXAxisLabel = true;
  xAxisLabel = 'Date Time';
  yAxisLabel = 'Measurement';
  timeline = false;

  colorScheme = {
    domain: ['#5AA454', '#E44D25', '#CFC0BB', '#7aa3e5', '#a8385d', '#aae3f5'],
  };

  src: SafeResourceUrl;
  logoUrl = DEFAULT_LOGO_PATH;

  // For Line Chart
  constructor(
    private activatedRoute: ActivatedRoute,
    private portService: PortService,
    private router: Router,
    public datepipe: DatePipe,
    private sanitizer: DomSanitizer
  ) {}

  ngAfterViewInit(): void {
    this.loadTenantSecondLogo();
    this.activatedRoute.params.subscribe(() => {
      const params = this.activatedRoute.snapshot?.parent?.params;
      this.portService.getExpandById(this.portId || params.id).subscribe(s => {
        this.port = s[0];
        const endDate = new Date();
        endDate.setHours(0, 0, 0, 0);
        endDate.setDate(endDate.getDate() + 4);
        this.portService
          .fetchWeatherInformationByIdAndUtcEndDate(this.port.id, endDate.toISOString())
          .subscribe(p => {
            if (p == null) return;
            this.weatherResponse = p;
            this.setUrl();
            this.weatherResponse.hours.forEach(hour => {
              if (this.todayWeather == null && this.isToday(hour.time)) {
                this.todayWeather = hour;
              }
              const date = hour.time.split('T')[0];
              if (date in this.weatherViewModel) {
                this.weatherViewModel[date].push(hour);
              } else {
                this.tabs.push({ name: date });
                this.weatherViewModel[date] = new Array(hour);
              }
            });
          });
      });
    });
  }

  buildDataSet(
    waveHeight: any[],
    wavePeriod: any[],
    swellHeight: any[],
    swellPeriod: any[],
    windSpeed: any[],
    windGust: any[]
  ) {
    return [
      {
        label: 'Wave Height',
        data: waveHeight,
        borderWidth: 1,
        pointRadius: 0,
        borderColor: '#96e698',
        backgroundColor: '#96e698',
        yAxisID: 'y',
      },
      {
        label: 'Swell Height',
        data: swellHeight,
        borderWidth: 1,
        pointRadius: 0,
        borderColor: '#4fa9dd',
        backgroundColor: '#4fa9dd',
        yAxisID: 'y',
      },
      {
        label: 'Wave Period',
        data: wavePeriod,
        borderWidth: 1,
        pointRadius: 0,
        borderColor: '#a473ed',
        backgroundColor: '#a473ed',
        yAxisID: 'y1',
      },
      {
        label: 'Swell Period',
        data: swellPeriod,
        borderWidth: 1,
        pointRadius: 0,
        borderColor: '#5ae5c9',
        backgroundColor: '#5ae5c9',
        yAxisID: 'y1',
      },
      {
        label: 'Wind Speed',
        data: windSpeed,
        borderWidth: 1,
        pointRadius: 0,
        borderColor: '#ee9b6d',
        backgroundColor: '#ee9b6d',
        yAxisID: 'y2',
      },
      {
        label: 'Wind Gust',
        data: windGust,
        borderWidth: 1,
        pointRadius: 0,
        borderColor: '#dcce55',
        backgroundColor: '#dcce55',
        yAxisID: 'y2',
      },
    ];
  }

  setConfig() : ChartOptions<'line'>  {
    return {
      responsive: true,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: this.setPlugins(),
      scales: this.setAxes(),
    };
  }

  setPlugins() {
    return {
      legend: {
        labels: {
          usePointStyle: true,
        },
      },
      title: {
        display: true,
        text: 'Weather Chart',
      },
    };
  }

  setAxes() {
    return {
      x: {
        stacked: false,
        title: {
          display: true,
          text: 'Date Time',
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        stacked: false,
        title: {
          display: true,
          text: 'Height (m)',
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        stacked: false,
        title: {
          display: true,
          text: 'Period (s)',
        },
      },
      y2: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        stacked: false,
        title: {
          display: true,
          text: 'Distance (m/s)',
        },
      },
    };
  }
  navigateBack() {
    this.router.navigate(['x/pg', 'port']);
  }

  isToday(date: Date | string) {
    const today = new Date();
    date = typeof date === 'string' ? new Date(date) : date;
    if (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    ) {
      return today.getHours() - date.getHours() <= 1;
    }
  }

  download() {
    // this.generateWeatherData();
    this.isVisible = true;
    setTimeout(async () => {
      const { default: Chart } = await import('chart.js/auto');
      this.canvas = document.getElementById('myChart');
      this.ctx = this.canvas.getContext('2d');
      let myChart = new Chart(this.ctx, {
        type: 'line',
        data: this.buildChartData(),
        options: this.setConfig(),
      });
    }, 100);
  }

  // Tenant's Second Logo (backend falls back to the Tenant Logo); keeps the default logo when neither exists
  private loadTenantSecondLogo() {
    const tenantId = localStorage.getItem('tenantId');
    if (!tenantId) return;

    const apiUrl = (environment.apis?.default?.url || '').replace(/\/$/, '');
    this.getBase64FromUrl(`${apiUrl}/api/app/tenant-info/logo/${tenantId}/second`)
      .then(dataUrl => (this.logoUrl = dataUrl))
      .catch(() => {});
  }

  weatherDownload() {
    this.getBase64FromUrl(this.logoUrl).then(async (base64Logo)=>{
       const docDefinition = this.buildPdfDocument(base64Logo);
       const pdfMake = await getPdfMake();
       pdfMake.createPdf(docDefinition).download('WeatherReport.pdf');
    });

  }

  buildPdfDocument(logo: string): any {
  const content: any[] = [];
  
  // Header
  content.push({
    columns: [
      {image: logo,
          width : 150,
        },
      {
        text: `${this.port?.name} Weather Report`,
        alignment: 'center',
        style: 'header',
        margin: [0, 30, 0, 0],
      }
    ],
    columnGap: 20,
  });

  content.push({ text: '\n\n' });

  // Create table body with header row only once
  const body = [
    [
      { text: 'Date Time', style: 'tableHeader' },
      { text: 'Air Temp', style: 'tableHeader' },
      { text: 'Water Temp', style: 'tableHeader' },
      { text: 'Humidity (%)', style: 'tableHeader' },
      { text: 'Visibility (km)', style: 'tableHeader' },
      { text: 'Wave Height (m)', style: 'tableHeader' },
      { text: 'Wave Period (s)', style: 'tableHeader' },
      { text: 'Swell Height (m)', style: 'tableHeader' },
      { text: 'Swell Period (s)', style: 'tableHeader' },
      { text: 'Wind Speed (m/s)', style: 'tableHeader' },
      { text: 'Wind Gust (m/s)', style: 'tableHeader' },
    ],
  ];

  // Add data rows from all tabs
  this.tabs.forEach((tab) => {
    const weatherData = this.weatherViewModel[tab.name];
    if (!weatherData || weatherData.length === 0) return;
    
    weatherData.forEach(data => {
      // Only add rows that have actual time data
      if (data?.time) {
        body.push([
          `${this.datepipe.transform(tab.name, 'dd MMM')} ${this.datepipe.transform(data.time, 'HH:mm')}`,
          data.airTemperature?.sg ?? '',
          data.waterTemperature?.sg ?? '',
          data.humidity?.sg ?? '',
          data.visibility?.sg ?? '',
          data.waveHeight?.sg ?? '',
          data.wavePeriod?.sg ?? '',
          data.swellHeight?.sg ?? '',
          data.swellPeriod?.sg ?? '',
          data.windSpeed?.sg ?? '',
          data.gust?.sg ?? '',
        ]);
      }
    });
  });

  // Add the table to content (only once, after all data is collected)
  content.push({
    style: 'table',
    table: {
      headerRows: 1,
      widths: Array(11).fill('*'),
      body: body,
    },
    layout: {
      fillColor: (rowIndex: number) => (rowIndex === 0 ? '#eeeeee' : null),
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#D3D3D3',
      vLineColor: () => '#D3D3D3',
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 2,
      paddingBottom: () => 2,
      dontBreakRows: true,
    },
  });

  const canvas = document.getElementById('myChart') as HTMLCanvasElement;
  if (canvas) {
    const chartImage = canvas.toDataURL('image/png');
    content.push({
      image: chartImage,
      width: 500,
      alignment: 'center',
      margin: [0, 20, 0, 0],
    });
  }

  return {
    pageSize: 'A4',
    pageMargins: [20, 40, 20, 40],
    content,
    styles: {
      header: {
        fontSize: 25,
        bold: true,
      },
      subheader: {
        fontSize: 14,
        margin: [0, 10, 0, 5],
        bold: true,
      },
      tableHeader: {
        bold: true,
        fontSize: 8,
        color: 'black',
        alignment: 'center',
      },
      table: {
        margin: [0, 5, 0, 15],
        fontSize: 8,
      },
    },
  };
}

getBase64FromUrl(url: string): Promise<string> {
  return fetch(url)
    .then(response => {
      if (!response.ok) throw new Error(`Logo request failed (${response.status})`);
      return response.blob();
    })
    .then(blob => new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    }));
}

  buildChartData() {
    const dateTime = [];
    const waveHeight = [];
    const wavePeriod = [];
    const swellHeight = [];
    const swellPeriod = [];
    const windSpeed = [];
    const windGust = [];
    this.tabs.forEach(tabs => {
      this.weatherViewModel[tabs.name].forEach(data => {
        dateTime.push(this.datepipe.transform(data.time, 'dd MMM HH:mm'));
        waveHeight.push(data.waveHeight.sg);
        wavePeriod.push(data.wavePeriod.sg);
        swellHeight.push(data.swellHeight.sg);
        swellPeriod.push(data.swellPeriod.sg);
        windSpeed.push(data.windSpeed.sg);
        windGust.push(data.gust.sg);
      });
    });
    return {
      labels: dateTime,
      datasets: this.buildDataSet(
        waveHeight,
        wavePeriod,
        swellHeight,
        swellPeriod,
        windSpeed,
        windGust
      ),
    };
  }

  setUrl() {
    const url = `//embed.windy.com/embed2.html?lat=${this.port.latitude}&lon=${this.port.longitude}&zoom=8&level=surface&overlay=wind&marker=true&location=coordinates&message=false`;
    this.src = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}

interface ChartHeader {
  name: string;
  series: ChartSeries[];
}
interface ChartSeries {
  name: string;
  value: number;
}
